import { useSettings } from 'connectors/Settings/Settings';
import { substringStripHtml } from 'helpers/parsing/substringStripHtml';
import { useDomainConfig } from 'hooks/useDomainConfig';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { OgTypeEnum } from 'types/seo';

type SeoMetaProps = {
    title?: string | null;
    description?: string | null;
    ogType?: OgTypeEnum | undefined;
    ogImageUrlDefault?: string | null | undefined;
};

const SeoMeta: FC<SeoMetaProps> = ({ title, description, ogType = OgTypeEnum.Website, ogImageUrlDefault }) => {
    const settings = useSettings();
    const currentUri = useRouter().asPath;
    const { url, defaultLocaleIdentifier } = useDomainConfig();
    const currentUrlWithDomain = url.substring(0, url.length - 1) + currentUri;

    const titleFromApi = settings?.seo.title;
    const descriptionFromApi = settings?.seo.metaDescription;
    const suffixFromApi = settings?.seo.titleAddOn;

    const pageTitle = substringStripHtml(title ?? titleFromApi + ' ' + suffixFromApi, 70);
    const metaDescription = description ?? descriptionFromApi;
    const pageImageUrl = ogImageUrlDefault ?? url + 'images/tescoma-white-logo.png';

    return (
        <Head>
            <title>{pageTitle}</title>
            {metaDescription && <meta name="description" content={substringStripHtml(metaDescription, 160)} />}

            <meta property="og:site_name" content="Tescoma e-shop" />
            <meta property="og:locale" content={defaultLocaleIdentifier.replace('-', '_')} />
            <meta property="og:type" content={ogType} />
            {pageTitle && <meta property="og:title" content={pageTitle} />}
            {metaDescription && <meta property="og:description" content={substringStripHtml(metaDescription, 160)} />}
            {pageImageUrl && <meta property="og:image" content={pageImageUrl} />}

            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:domain" content={url} />
            <meta property="twitter:url" content={currentUrlWithDomain} />
            {pageTitle && <meta property="twitter:title" content={pageTitle} />}
            {metaDescription && (
                <meta property="twitter:description" content={substringStripHtml(metaDescription, 160)} />
            )}
            {pageImageUrl && <meta property="twitter:image" content={pageImageUrl} />}
        </Head>
    );
};

export default SeoMeta;
